import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

// Imported assets
import { CheckmarkIcon, HideIcon, UnhideIcon } from "../../../assets/icons/svgs/Icons";
import CloseIcon from "../../../assets/icons/Close_Red.svg";
import StrataLogo from "../../../assets/images/STRATALogo_Black.png";

// Imported components
import Info from "../../../components/form/Info";
import Label from "../../../components/form/Label";
import PrimaryButton from "../../../components/ui/PrimaryButton";

// Imported contexts
import { useUserContext } from "../../../contexts/User";

// Imported interfaces
import { ResetPasswordInputErrors } from "../../../contexts/User";

// Styled components
const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 20px;
  justify-content: center;
  min-height: 100vh;
  overflow-y: auto;
  padding: 20px;
  width: 100vw;
`;

const Description = styled.div`
  font-family: Inter, Oxygen, Roboto, sans-serif;
  font-size: 16px;
`;

const ErrorInfo = styled(Info)`
  color: red;
`;

const Form = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FormField = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: flex-start;
  padding: 2px;
  position: relative;
  width: 100%;

  & * {
    font-family: Inter, Oxygen, Roboto, sans-serif !important;
  }
`;

const FormRow = styled.div`
  align-items: flex-start;
  align-self: stretch;
  display: flex;
  flex-direction: row;
  gap: 80px;
  justify-content: center;
  margin: 10px 0;
  position: relative;
`;

const Icon = styled.img`
  height: auto;
  margin-right: 10px;
  width: 20px;

  &:hover {
    cursor: pointer;
  }
`;

const Logo = styled.img`
  cursor: pointer;
  height: auto;
  left: 0;
  margin: max(16px, 2vw);
  position: absolute;
  top: 0;
  width: max(128px, 8vw);
`;

const PasswordRequirementsContainer = styled.div`
  display: flex;
  flex-direction: column;
  font-family: Inter, Oxygen, Roboto, sans-serif !important;
  gap: 5px;
  margin: 20px 0;
`;

const PasswordRequirementsHeader = styled.p`
  color: rgba(0, 0, 0, 0.5);
`;

const PasswordRequirementsText = styled.p`
  align-items: center;
  color: rgba(0, 0, 0, 1);
  display: flex;
  flex-direction: row;
`;

const RequirementsIcon = styled(Icon)`
  cursor: default;

  &:hover {
    cursor: default;
  }
`;

const ResetButton = styled(PrimaryButton)`
  background: var(--terbine-blue);

  &:disabled {
    background: lab(from var(--terbine-dark-gray) calc(l + 25) a b / 100);
  }

  &:hover:disabled {
    cursor: not-allowed;
  }

  &:hover:enabled {
    background: lab(from var(--terbine-blue) calc(l - 5) a b / 100);
  }
`;

const ShowIcon = styled.div`
  cursor: pointer;
`;

const Title = styled.div`
  font-family: Oxygen, Inter, Roboto, sans-serif;
  font-size: 32px;
  font-weight: bolder;
`;

const ResetPassword = () => {
  const navigate = useNavigate();

  const {
    /*changePassword, */ user,
    /*passwordMax, */ passwordMin,
    validateInputs,
  } = useUserContext();

  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");

  const [resetPasswordErrs, setResetPasswordErrs] =
    useState<ResetPasswordInputErrors | null>(null);

  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);

  useEffect(() => {
    setResetPasswordErrs(
      validateInputs("ResetPassword", {
        newPassword: newPassword,
        confirmPassword: confirmPassword,
      })
    );
  }, [newPassword, confirmPassword, setResetPasswordErrs]);

  const toggleNewPassword = useCallback(() => {
    setShowNewPassword(!showNewPassword);
  }, [setShowNewPassword, showNewPassword]);

  const toggleConfirmPassword = useCallback(() => {
    setShowConfirmPassword(!showConfirmPassword);
  }, [setShowConfirmPassword, showConfirmPassword]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // TODO: Handle reset password submit here
  };

  return (
    <Container>
      <Logo onClick={() => navigate("/login")} src={StrataLogo} />

      <Title>Reset Password</Title>
      <Description>Enter a new password below.</Description>

      <Form onSubmit={(e) => handleSubmit(e)}>
        <FormRow>
          <FormField>
            <Label>New Password</Label>
            <FormControl sx={{ width: "500px" }}>
              <TextField
                onChange={(e) => setNewPassword(e.target.value)}
                size="small"
                slotProps={{
                  htmlInput: {
                    // maxLength: passwordMax,
                    minLength: passwordMin,
                  },
                  input: {
                    endAdornment: (
                      <ShowIcon onClick={() => toggleNewPassword()}>
                        {showNewPassword ? (
                          <UnhideIcon fill="black" />
                        ) : (
                          <HideIcon fill="black" />
                        )}
                      </ShowIcon>
                    ),
                  },
                }}
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                variant="outlined"
              />
            </FormControl>
          </FormField>
        </FormRow>

        <FormRow>
          <FormField>
            <Label $hasError={Boolean(resetPasswordErrs?.newAndConfirmMatchErr)}>Confirm New Password</Label>
            <FormControl sx={{ width: "500px" }}>
              <TextField
                error={Boolean(resetPasswordErrs?.newAndConfirmMatchErr)}
                onChange={(e) => setConfirmPassword(e.target.value)}
                size="small"
                slotProps={{
                  htmlInput: {
                    // maxLength: passwordMax,
                    minLength: passwordMin,
                  },
                  input: {
                    endAdornment: (
                      <ShowIcon onClick={() => toggleConfirmPassword()}>
                        {showConfirmPassword ? (
                          <UnhideIcon fill="black" />
                        ) : (
                          <HideIcon fill="black" />
                        )}
                      </ShowIcon>
                    ),
                  },
                }}
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                variant="outlined"
              />
              {resetPasswordErrs?.newAndConfirmMatchErr && (
                <ErrorInfo>
                  {resetPasswordErrs?.newAndConfirmMatchErr}
                </ErrorInfo>
              )}
            </FormControl>
          </FormField>
        </FormRow>

        <PasswordRequirementsContainer>
          <PasswordRequirementsHeader>
            Password Requirements
          </PasswordRequirementsHeader>
          <PasswordRequirementsText>
            {resetPasswordErrs?.newPasswordErrs?.lengthErr ? (
              <RequirementsIcon src={CloseIcon} />
            ) : (
              <div>
                <CheckmarkIcon fill="#13c300" width="20px" />
              </div>
            )}{" "}
            At least 12 characters in length
          </PasswordRequirementsText>
          <PasswordRequirementsText>
            {resetPasswordErrs?.newPasswordErrs?.lowerErr ? (
              <RequirementsIcon src={CloseIcon} />
            ) : (
              <div>
                <CheckmarkIcon fill="#13c300" width="20px" />
              </div>
            )}{" "}
            At least 1 lower case character
          </PasswordRequirementsText>
          <PasswordRequirementsText>
            {resetPasswordErrs?.newPasswordErrs?.upperErr ? (
              <RequirementsIcon src={CloseIcon} />
            ) : (
              <div>
                <CheckmarkIcon fill="#13c300" width="20px" />
              </div>
            )}{" "}
            At least 1 upper case character
          </PasswordRequirementsText>
          <PasswordRequirementsText>
            {resetPasswordErrs?.newPasswordErrs?.numErr ? (
              <RequirementsIcon src={CloseIcon} />
            ) : (
              <div>
                <CheckmarkIcon fill="#13c300" width="20px" />
              </div>
            )}{" "}
            At least 1 number
          </PasswordRequirementsText>
          <PasswordRequirementsText>
            {resetPasswordErrs?.newPasswordErrs?.specialErr ? (
              <RequirementsIcon src={CloseIcon} />
            ) : (
              <div>
                <CheckmarkIcon fill="#13c300" width="20px" />
              </div>
            )}{" "}
            At least 1 special character
          </PasswordRequirementsText>
          <PasswordRequirementsText>
            {resetPasswordErrs?.newPasswordErrs?.recentErr ? (
              <RequirementsIcon src={CloseIcon} />
            ) : (
              <div>
                <CheckmarkIcon fill="#13c300" width="20px" />
              </div>
            )}{" "}
            May not use a password used in last 6 months
          </PasswordRequirementsText>
        </PasswordRequirementsContainer>

        <ResetButton
          disabled={
            !!(
              resetPasswordErrs?.newPasswordErrs?.lengthErr ||
              resetPasswordErrs?.newPasswordErrs?.lowerErr ||
              resetPasswordErrs?.newPasswordErrs?.upperErr ||
              resetPasswordErrs?.newPasswordErrs?.numErr ||
              resetPasswordErrs?.newPasswordErrs?.specialErr ||
              resetPasswordErrs?.newPasswordErrs?.recentErr ||
              resetPasswordErrs?.newAndConfirmMatchErr
            )
          }
          onClick={(e) => handleSubmit(e)}
          type="submit"
        >
          Reset Password
        </ResetButton>
      </Form>
    </Container>
  );
};

export default ResetPassword;
